import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  startClock,
  stopClock,
  subscribeMyClockState,
  queueTimeClockMutation,
  isNetworkFailure,
  type StartClockResult,
  type StopClockResult,
} from '../services/timesheetsService'
import {
  listPendingMutations,
  type HelperMutationRecord,
  type MobileOutboxActorRole,
  type TimeClockPayload,
} from '../services/helper/helperOutboxDb'
import type { ClockState, GeoPoint } from '../types/timesheets'
import { getSharedDeviceWorkRouterContext } from '../services/sharedDeviceWorkRouter'
import {
  getActivePersonCache,
  subscribeActivePersonCache,
  updateActivePersonCache,
  type SharedDevicePersonSnapshot,
} from '../services/sharedDevicePersonCache'
import { useNls } from '../nls/nlsProvider'
import { ToastContext, type ToastVariant } from '../context/toastContextCore'

export { isNetworkFailure }

function generateClientClockId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `tc_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

export interface UseMyClockOptions {
  workspaceId: string
  uid?: string | null
  actorRole?: MobileOutboxActorRole
}

export interface UseMyClockResult {
  clock: ClockState | null
  isPending: boolean
  loading: boolean
  clockIn: (options?: { nodeId?: string | null; geo?: GeoPoint | null; device?: string | null; kind?: 'CLOCK' | 'TIMER' }) => Promise<StartClockResult | void>
  startTimer: (options: { nodeId: string; device?: string | null }) => Promise<StartClockResult | void>
  clockOut: (options?: { geo?: GeoPoint | null; note?: string | null }) => Promise<StopClockResult | void>
  stopTimer: (options?: { note?: string | null }) => Promise<StopClockResult | void>
  refreshOutbox: () => Promise<void>
}

/**
 * Hook that merges the live Firestore time_clock_state with pending TIME_CLOCK outbox mutations.
 * When online, clock in/out calls execute the callable directly.
 * On network failure or when offline, calls queue into the shared mobile outbox.
 * While pending sync:
 * - Pending START: card shows running since occurredAt with "Waiting to sync" chip.
 * - Pending STOP: card shows clocked out with "Waiting to sync" chip.
 */
export function useMyClock({
  workspaceId,
  uid,
  actorRole = 'MEMBER',
}: UseMyClockOptions): UseMyClockResult {
  const { t } = useNls()
  const toastContext = useContext(ToastContext)
  const showToast = useCallback((msg: string, type?: ToastVariant) => {
    toastContext?.showToast?.(msg, type)
  }, [toastContext])

  const routerCtx = getSharedDeviceWorkRouterContext()
  const [cachedSnapshot, setCachedSnapshot] = useState<SharedDevicePersonSnapshot | null>(
    () => getActivePersonCache()?.snapshot ?? null,
  )
  const [localOfflineClock, setLocalOfflineClock] = useState<ClockState | null>(null)

  useEffect(() => {
    return subscribeActivePersonCache((snap) => {
      setCachedSnapshot(snap)
    })
  }, [])

  const [liveClock, setLiveClock] = useState<ClockState | null>(null)
  const [loading, setLoading] = useState(true)
  const [pendingRecords, setPendingRecords] = useState<HelperMutationRecord[]>([])

  useEffect(() => {
    if (!workspaceId || !uid) {
      return
    }
    const unsub = subscribeMyClockState(workspaceId, uid, (clock) => {
      setLiveClock(clock)
      setLoading(false)
    })
    return () => {
      unsub()
      setLiveClock(null)
    }
  }, [workspaceId, uid])

  const refreshOutbox = useCallback(async () => {
    if (!workspaceId) return
    try {
      const pending = await listPendingMutations()
      const matching = pending
        .filter(
          (m) =>
            m.type === 'TIME_CLOCK' &&
            (m.payload as TimeClockPayload).workspaceId === workspaceId &&
            (!uid || (m.payload as TimeClockPayload).actorId === uid),
        )
        .sort((a, b) => a.createdAt - b.createdAt)
      setPendingRecords(matching)
    } catch {
      setPendingRecords([])
    }
  }, [workspaceId, uid])

  useEffect(() => {
    const timer = setTimeout(() => {
      void refreshOutbox()
    }, 0)
    if (typeof window === 'undefined') {
      return () => clearTimeout(timer)
    }
    const onOnline = () => void refreshOutbox()
    const onCustom = () => void refreshOutbox()
    window.addEventListener('online', onOnline)
    window.addEventListener('time-clock-outbox-changed', onCustom)
    const interval = setInterval(() => void refreshOutbox(), 5000)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('online', onOnline)
      window.removeEventListener('time-clock-outbox-changed', onCustom)
      clearInterval(interval)
    }
  }, [refreshOutbox])

  const { clock, isPending } = useMemo(() => {
    if (routerCtx) {
      const offlineClock =
        (cachedSnapshot?.clockState as ClockState | null | undefined) ?? localOfflineClock
      if (offlineClock) {
        return { clock: offlineClock, isPending: true }
      }
      return { clock: null, isPending: false }
    }

    if (pendingRecords.length === 0) {
      return { clock: liveClock, isPending: false }
    }
    const latest = pendingRecords[pendingRecords.length - 1]
    const payload = latest.payload as TimeClockPayload
    if (payload.action === 'START') {
      const optimisticClock: ClockState = {
        id: payload.clientGeneratedId,
        workspaceId: payload.workspaceId,
        uid: uid ?? payload.actorId ?? '',
        kind: payload.kind ?? 'CLOCK',
        nodeId: payload.nodeId ?? null,
        startedAt: payload.occurredAt,
        geoIn: payload.geo ?? null,
        device: payload.device ?? null,
        occurredAtSource: 'CLIENT',
      }
      return { clock: optimisticClock, isPending: true }
    } else {
      // STOP
      return { clock: null, isPending: true }
    }
  }, [routerCtx, localOfflineClock, cachedSnapshot?.clockState, liveClock, pendingRecords, uid])

  useEffect(() => {
    if (routerCtx) return
    const active = getActivePersonCache()
    if (!active) return
    const timer = setTimeout(() => {
      void updateActivePersonCache((prev) => ({
        ...prev,
        clockState: clock ? { ...clock, running: true } : null,
      }))
    }, 500)
    return () => clearTimeout(timer)
  }, [routerCtx, clock])

  const clockIn = useCallback(
    async (options?: { nodeId?: string | null; geo?: GeoPoint | null; device?: string | null; kind?: 'CLOCK' | 'TIMER' }): Promise<StartClockResult | void> => {
      const occurredAt = new Date().toISOString()
      const clientGeneratedId = generateClientClockId()
      const isOnlineNow = typeof navigator !== 'undefined' ? navigator.onLine : true
      const kind = options?.kind ?? 'CLOCK'

      if (routerCtx) {
        await queueTimeClockMutation({
          workspaceId,
          action: 'START',
          clientGeneratedId,
          occurredAt,
          kind,
          nodeId: options?.nodeId ?? null,
          geo: options?.geo ?? null,
          device: options?.device ?? 'mobile',
          actorId: uid ?? routerCtx.personUid,
          actorRole,
        })
        const newClock: ClockState = {
          id: clientGeneratedId,
          workspaceId,
          uid: uid ?? routerCtx.personUid,
          kind,
          nodeId: options?.nodeId ?? null,
          startedAt: occurredAt,
          geoIn: options?.geo ?? null,
          device: options?.device ?? 'mobile',
          occurredAtSource: 'CLIENT',
        }
        setLocalOfflineClock(newClock)
        void updateActivePersonCache((prev) => ({
          ...prev,
          clockState: { ...newClock, running: true },
        }))
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('time-clock-outbox-changed'))
        }
        return {
          success: true,
          clock: newClock,
        }
      }

      if (!isOnlineNow) {
        await queueTimeClockMutation({
          workspaceId,
          action: 'START',
          clientGeneratedId,
          occurredAt,
          kind,
          nodeId: options?.nodeId ?? null,
          geo: options?.geo ?? null,
          device: options?.device ?? 'mobile',
          actorId: uid ?? undefined,
          actorRole,
        })
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('time-clock-outbox-changed'))
        }
        return
      }

      try {
        const res = await startClock({
          workspaceId,
          clientGeneratedId,
          kind,
          nodeId: options?.nodeId ?? null,
          geoIn: options?.geo ?? null,
          device: options?.device ?? 'mobile',
          occurredAt,
        })
        return res
      } catch (err) {
        if (isNetworkFailure(err) || !navigator.onLine) {
          await queueTimeClockMutation({
            workspaceId,
            action: 'START',
            clientGeneratedId,
            occurredAt,
            kind,
            nodeId: options?.nodeId ?? null,
            geo: options?.geo ?? null,
            device: options?.device ?? 'mobile',
            actorId: uid ?? undefined,
            actorRole,
          })
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('time-clock-outbox-changed'))
          }
        } else {
          throw err
        }
      }
    },
    [routerCtx, workspaceId, uid, actorRole],
  )

  const startTimer = useCallback(
    async (options: { nodeId: string; device?: string | null }): Promise<StartClockResult | void> => {
      return clockIn({
        nodeId: options.nodeId,
        device: options.device ?? 'web',
        kind: 'TIMER',
      })
    },
    [clockIn],
  )

  const clockOut = useCallback(
    async (options?: { geo?: GeoPoint | null; note?: string | null }): Promise<StopClockResult | void> => {
      const occurredAt = new Date().toISOString()
      const clientGeneratedId = generateClientClockId()
      const isOnlineNow = typeof navigator !== 'undefined' ? navigator.onLine : true

      if (routerCtx) {
        const activeClock =
          (cachedSnapshot?.clockState as ClockState | null | undefined) ??
          localOfflineClock ??
          clock
        if (!activeClock) {
          showToast(t('NLSOpsSharedDeviceClockNoRunning'), 'error')
          return
        }
        await queueTimeClockMutation({
          workspaceId,
          action: 'STOP',
          clientGeneratedId,
          occurredAt,
          kind: 'CLOCK',
          geo: options?.geo ?? null,
          device: 'mobile',
          actorId: uid ?? routerCtx.personUid,
          actorRole,
        })
        setLocalOfflineClock(null)
        void updateActivePersonCache((prev) => ({
          ...prev,
          clockState: null,
        }))
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('time-clock-outbox-changed'))
        }
        return {
          success: true,
          entry: null,
        }
      }

      if (!isOnlineNow) {
        await queueTimeClockMutation({
          workspaceId,
          action: 'STOP',
          clientGeneratedId,
          occurredAt,
          kind: 'CLOCK',
          geo: options?.geo ?? null,
          device: 'mobile',
          actorId: uid ?? undefined,
          actorRole,
        })
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('time-clock-outbox-changed'))
        }
        return
      }

      try {
        const res = await stopClock({
          workspaceId,
          clientGeneratedId,
          geoOut: options?.geo ?? null,
          note: options?.note ?? null,
          occurredAt,
        })
        return res
      } catch (err) {
        if (isNetworkFailure(err) || !navigator.onLine) {
          await queueTimeClockMutation({
            workspaceId,
            action: 'STOP',
            clientGeneratedId,
            occurredAt,
            kind: 'CLOCK',
            geo: options?.geo ?? null,
            device: 'mobile',
            actorId: uid ?? undefined,
            actorRole,
          })
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('time-clock-outbox-changed'))
          }
        } else {
          throw err
        }
      }
    },
    [routerCtx, localOfflineClock, cachedSnapshot?.clockState, clock, workspaceId, uid, actorRole, showToast, t],
  )

  const stopTimer = useCallback(
    async (options?: { note?: string | null }): Promise<StopClockResult | void> => {
      return clockOut({
        note: options?.note ?? null,
      })
    },
    [clockOut],
  )

  return {
    clock,
    isPending,
    loading,
    clockIn,
    startTimer,
    clockOut,
    stopTimer,
    refreshOutbox,
  }
}
